import UnleashService from '@/services/unleash'
import sentry from '@/3rd-parties/sentry'
import UnleashFlagTurnOnTime from '@/constants/UnleashFlagTurnOnTime'

const { unleashClient } = UnleashService

const _state = {
  features: {}, // Store feature toggles
  isInitialized: false, // Track whether the client has initialized
  loading: {
    initialization: false,
    contextUpdate: false,
  },
  contextUpdateCounter: 0, // Counter for contextUpdate operations
  context: {},
}

const mutations = {
  setFeatures(state, features) {
    const featureMap = features.reduce((map, feature) => {
      map[feature.name] = feature
      return map
    }, {})

    state.features = featureMap
  },
  setInitialized(state, initialized) {
    state.isInitialized = initialized
  },
  setLoading(state, { type, loading }) {
    if (type === 'contextUpdate') {
      if (loading) {
        state.contextUpdateCounter += 1
      } else {
        state.contextUpdateCounter = Math.max(0, state.contextUpdateCounter - 1)
      }

      state.loading.contextUpdate = state.contextUpdateCounter > 0
    } else {
      state.loading[type] = loading
    }
  },
  setContext(state, context) {
    state.context = {
      ...state.context,
      ...context,
    }
  },
}

const actions = {
  async initializeFeatureToggles({ commit }) {
    commit('setLoading', { type: 'initialization', loading: true })

    try {
      // Handle the ready event to fetch features
      unleashClient.on('ready', () => {
        const features = unleashClient.getAllToggles()
        commit('setFeatures', features)
        commit('setInitialized', true)
        commit('setLoading', { type: 'initialization', loading: false })
      })
    } catch (error) {
      commit('setLoading', false)
      sentry.captureException(error)
    } finally {
      commit('setLoading', { type: 'initialization', loading: false })
    }
  },
  async updateFeatureContext({ state, commit }, context) {
    commit('setLoading', { type: 'contextUpdate', loading: true })

    commit('setContext', context)

    try {
      if (unleashClient) {
        await unleashClient.updateContext(state.context)
        const features = unleashClient.getAllToggles()

        commit('setFeatures', features)
      }
    } catch (error) {
      sentry.captureException(error)
    } finally {
      commit('setLoading', { type: 'contextUpdate', loading: false })
      if (context.onReady) {
        context.onReady()
      }
    }
  },
}

const _getters = {
  isFeatureEnabled: (state) => (featureName) => {
    return state.features[featureName] && state.features[featureName].enabled
  },
  getVariant: (state) => (featureName) => {
    return state.features[featureName] ? state.features[featureName].variant : {
      name: 'disabled',
      enabled: false,
      payload: null,
    }
  },
  isUnleashInitialized: (state) => {
    return state.isInitialized
  },
  isLoadingFeatures: (state) => {
    return state.loading.initialization || state.loading.contextUpdate
  },
  isPresentationCreatedAfterTurnOnFlag: (_, __, rootState) => {
    return (flag) => {
      if (!rootState.presentation || !flag || !UnleashFlagTurnOnTime[flag]) return false
      const flagTurnOnTime = UnleashFlagTurnOnTime[flag]
      return new Date(rootState.presentation.createdAt) > flagTurnOnTime
    }
  },

  canSubscribeMqttChangeSlideEvent: (_, getters, rootState) => {
    return getters.isFeatureEnabled('AHA-30192-presenter-change-slide') && !rootState.presentation.audiencePacing
  },
  canUseNewChangeSlideMechanism: (_, getters, rootState) => {
    return getters.isFeatureEnabled('AHA-30192-presenter-change-slide') && !rootState.presentation.audiencePacing
  },
  canSelfPaceUseNewChangeSlideMechanism: (_, getters) => {
    return getters.isFeatureEnabled('AHA-30192-presenter-change-slide')
  },
  // khi enable flag này lên thì sẽ tắt tính năng Q&A, quiz streak,
  // everyone has answered để đảm bảo số lượng lớn audiences tham gia
  enableForBigEvent: (_, getters) => {
    return getters.isFeatureEnabled('enable-for-big-event')
  },
  turnOffReactionSaveDB: (_, getters) => {
    return getters.isFeatureEnabled('AHA-39736-off-save-reactions-to-db')
  },
  disableUpdateQuizStatusFromQuestionScreenToLobbyScreen: (_, getters) => {
    return getters.isFeatureEnabled('disable-update-quiz-status-from-question-screen-to-lobby-screen')
  },
  canUseEveryoneHasAnswered: (_, __, ___, rootGetters) => {
    return rootGetters['liveStatsV2/canUseLiveStatsForQuizV2']
  },

  canUseLogFile: (_, getters) => {
    return getters.isFeatureEnabled('AHA-35479-setup-logging-lib')
  },

  // Device Id terminology are often surfaced to the public eyes as universal audience id to avoid controversies,
  // but essentially that's the same as device id and that's how we're gonna address it internally.
  canUseDeviceId: (_, getters) => {
    return getters.isFeatureEnabled('universal-audience-id')
  },
  canUseCookieToLinkAudiences: (_, getters) => {
    return getters.isFeatureEnabled('can-use-cookie-to-link-audiences')
  },
  shouldPushDeviceAssociations: (_, getters) => {
    return getters.isFeatureEnabled('push-audience-id-associations')
  },
  shouldPushCookieAssociations: (_, getters) => {
    return getters.isFeatureEnabled('push-audience-id-cookie-associations')
  },
  canUseXmasDecoration: (_, getters) => {
    return getters.isFeatureEnabled('kill-switch-xmas-decoration')
  },
  canFixSubmitQuizIssue: (_, getters) => {
    return getters.isFeatureEnabled('fix-quiz-submit-button')
  },
  canCheckQuizSlideId: (_, getters) => {
    return getters.isFeatureEnabled('release-check-quiz-slide')
  },
  canUseSkipSlide: (_, getters) => {
    return getters.isFeatureEnabled('AHA-39045-skip-slide')
  },
  canUseFixMqxMissMessageSubmitQuiz: (_, getters) => {
    return getters.isFeatureEnabled('AHA-39885-audience-fix-mqx-not-response-quiz-answer')
  },
  canUseSlidePlugin: (_, getters) => {
    return getters.isFeatureEnabled('AHA-39386-aha-slide-plugin')
  },
  // Audience-side preload keepalive for plugin iframes: keeps the active plugin
  // iframe alive and boots the NEXT slide's iframe hidden + inert, so a forward
  // navigation shows it instantly with no reload. Requires the base slide-plugin
  // flag, and is disabled under audience pacing (the preload only warms the
  // presenter-driven forward slide, mirroring nextback.prefetchNextSlideAction).
  // OFF => the original single SlidePluginContent render path is unchanged.
  canUsePreloadSlidePlugin: (_, getters, rootState) => {
    return getters.canUseSlidePlugin
      && getters.isFeatureEnabled('AHA-46422-audience-preload-slide-plugin')
      && !rootState.presentation.audiencePacing
  },
  canFixFlickerOnReload: (_, getters) => {
    return getters.isFeatureEnabled('AHA-40971-audience-flicker-reload')
  },
  canFixQuizTimer: (_, getters) => {
    return getters.isFeatureEnabled('AHA-39296-fix-quiz-timer')
  },
  canUsePresentationGrouping: (_, getters) => {
    return getters.isFeatureEnabled('can-use-presentation-grouping')
  },
  // Kill-switch for the answer-submission resilience fix (retry transient network
  // failures + surface a failed submit so the audience can retry instead of
  // silently dropping the answer). Turning this off restores the pre-fix behavior.
  canUseSubmitAnswerRetry: (_, getters) => {
    return getters.isFeatureEnabled('AHA-43619-submit-answer-retry')
  },
  // Replaces the "My Answers" tab with an embed of the report app's participant
  // view, which renders every slide type instead of only the 5 quiz types.
  // Turning this off restores the existing MyQuizAnswers list unchanged.
  // Also switches the attended-presentations list/detail onto the v2 endpoints, which derive
  // attendance from "Audiences" so a record follows the session snapshot that a
  // multi-presenting-session moves it to.
  canUseEmbedMyAnswers: (_, getters) => {
    return getters.isFeatureEnabled('AHA-46642-release-embed-my-answer')
  },
  canUseHamburgerMenu: (_, getters) => {
    return !!getters.isFeatureEnabled('release-AHA-46910-audience-hamburger-menu')
  },
  canAutoAssignTeamsDefaultOn: (_, getters) => {
    return getters.isFeatureEnabled('AHA-43163-auto-assign-teams-default')
  },
}

export const isFeatureEnabled = (rootGetters, featureName) => {
  return rootGetters['featureToggles/isFeatureEnabled'](featureName)
}

export const getVariant = (rootGetters, featureName) => {
  return rootGetters['featureToggles/getVariant'](featureName)
}

export const isPresentationCreatedAfterTurnOnFlag = (rootGetters, flag) => {
  return rootGetters['featureToggles/isPresentationCreatedAfterTurnOnFlag'](flag)
}

export const isPresentationResetAfterTurnOnFlag = (presentation, flag) => {
  if (!presentation ||
    !presentation.resetTimeStamp ||
    !flag ||
    !UnleashFlagTurnOnTime[flag]
  ) {
    return false
  }
  // resetTimeStamp is a string, so need to convert it to number before parsing to Date object
  return new Date(Number(presentation.resetTimeStamp)) > UnleashFlagTurnOnTime[flag]
}

export default {
  namespaced: true,
  state: _state,
  mutations,
  actions,
  getters: _getters,
}
